import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { ArticleCard, CategoryPill, ReadingMeta } from "../components/ArticleCard";
import MediaEmbed from "../components/MediaEmbed";
import { Share2, Bookmark, Volume2, Twitter, Facebook, MessageCircle, ChevronRight, Linkedin, Send } from "lucide-react";
import { toast } from "sonner";

export default function ArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [trending, setTrending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [speaking, setSpeaking] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/articles/slug/${slug}`)
      .then((r) => {
        setArticle(r.data);
        return api.get("/articles", { params: { category: r.data.category, limit: 4 } });
      })
      .then((r) => setRelated((r?.data || []).filter((x) => x.slug !== slug).slice(0, 3)))
      .finally(() => setLoading(false));
    api.get("/articles", { params: { trending: true, limit: 5 } })
      .then((r) => setTrending(r.data || []));
    window.scrollTo(0, 0);
  }, [slug]);

  const share = (where) => {
    const url = window.location.href;
    const text = article?.title || "Bodh Networks";
    const share = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(text + " " + url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      telegram: `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
    }[where];
    if (share) window.open(share, "_blank");
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(window.location.href); toast.success("लिंक कॉपी हुआ!"); }
    catch { toast.error("कॉपी नहीं हो सका"); }
  };

  const speak = () => {
    if (!article) return;
    if (speaking) {
      window.speechSynthesis.cancel();
      setSpeaking(false);
      return;
    }
    const u = new SpeechSynthesisUtterance(`${article.title}. ${article.subtitle || ""}. ${article.content}`);
    u.lang = "hi-IN";
    u.onend = () => setSpeaking(false);
    window.speechSynthesis.speak(u);
    setSpeaking(true);
  };

  if (loading) return <div className="max-w-4xl mx-auto px-4 py-20 animate-pulse"><div className="h-8 bg-zinc-100 rounded w-3/4 mb-4" /><div className="h-72 bg-zinc-100 rounded-2xl mb-6" /><div className="h-4 bg-zinc-100 rounded w-full mb-2" /><div className="h-4 bg-zinc-100 rounded w-5/6" /></div>;
  if (!article) return <div className="max-w-4xl mx-auto px-4 py-20 text-center font-devanagari">खबर नहीं मिली.</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8" data-testid={`article-page-${article.id}`}>
      <nav className="flex items-center gap-2 text-xs font-hind text-zinc-500 mb-4">
        <Link to="/" className="hover:text-[#E30613]">Home</Link>
        <ChevronRight className="h-3 w-3" />
        <Link to={`/category/${article.category}`} className="hover:text-[#E30613]"><CategoryPill slug={article.category} /></Link>
      </nav>

      <div className="grid lg:grid-cols-12 gap-10">
        <article className="lg:col-span-8">
          <h1 className="font-devanagari font-black text-3xl sm:text-4xl lg:text-5xl text-zinc-900 leading-tight">{article.title}</h1>
          {article.subtitle && <p className="mt-3 font-devanagari text-lg text-zinc-600">{article.subtitle}</p>}

          <div className="mt-5 flex items-center justify-between border-y border-zinc-100 py-4">
            <ReadingMeta time={article.reading_time} author={article.author} dt={article.created_at} />
            <div className="flex items-center gap-1.5">
              <button onClick={speak} data-testid="article-tts" className={`h-9 w-9 rounded-full flex items-center justify-center transition ${speaking ? "bodh-gradient text-white" : "border border-zinc-200 hover:bodh-gradient hover:text-white"}`} title="सुनिए"><Volume2 className="h-4 w-4" /></button>
              <button onClick={() => share("twitter")} data-testid="share-twitter" className="h-9 w-9 rounded-full border border-zinc-200 flex items-center justify-center hover:bodh-gradient hover:text-white transition"><Twitter className="h-4 w-4" /></button>
              <button onClick={() => share("facebook")} data-testid="share-facebook" className="h-9 w-9 rounded-full border border-zinc-200 flex items-center justify-center hover:bodh-gradient hover:text-white transition"><Facebook className="h-4 w-4" /></button>
              <button onClick={() => share("whatsapp")} data-testid="share-whatsapp" className="h-9 w-9 rounded-full border border-zinc-200 flex items-center justify-center hover:bodh-gradient hover:text-white transition"><MessageCircle className="h-4 w-4" /></button>
              <button onClick={() => share("linkedin")} data-testid="share-linkedin" className="h-9 w-9 rounded-full border border-zinc-200 flex items-center justify-center hover:bodh-gradient hover:text-white transition"><Linkedin className="h-4 w-4" /></button>
              <button onClick={() => share("telegram")} data-testid="share-telegram" className="h-9 w-9 rounded-full border border-zinc-200 flex items-center justify-center hover:bodh-gradient hover:text-white transition"><Send className="h-4 w-4" /></button>
              <button onClick={copy} data-testid="share-copy" className="h-9 w-9 rounded-full border border-zinc-200 flex items-center justify-center hover:bodh-gradient hover:text-white transition"><Share2 className="h-4 w-4" /></button>
            </div>
          </div>

          {article.cover_image && (
            <div className="mt-6 rounded-3xl overflow-hidden bg-zinc-100">
              <img src={article.cover_image} alt={article.title} className="w-full h-auto max-h-[520px] object-cover" />
            </div>
          )}

          {article.video_url && <MediaEmbed url={article.video_url} />}

          <div className="bodh-article font-devanagari mt-8 text-zinc-800">
            {article.content.split("\n").filter(Boolean).map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>

          {article.tags?.length > 0 && (
            <div className="mt-8 flex flex-wrap gap-2">
              {article.tags.map((t) => (
                <span key={t} className="px-3 py-1 rounded-full bg-zinc-100 text-zinc-700 text-sm font-devanagari">#{t}</span>
              ))}
            </div>
          )}

          {related.length > 0 && (
            <div className="mt-14">
              <h3 className="font-devanagari font-black text-2xl mb-5">संबंधित खबरें</h3>
              <div className="grid sm:grid-cols-3 gap-5">
                {related.map((a) => <ArticleCard key={a.id} a={a} size="md" />)}
              </div>
            </div>
          )}
        </article>

        <aside className="lg:col-span-4">
          <div className="lg:sticky lg:top-32 space-y-6">
            <div className="rounded-2xl bg-gradient-to-br from-zinc-50 to-white border border-zinc-100 p-5">
              <div className="flex items-center gap-2 mb-4">
                <span className="font-outfit font-black uppercase tracking-widest text-xs bodh-text-gradient">Trending Now</span>
                <span className="flex-1 bodh-divider" />
              </div>
              <div className="space-y-4">
                {trending.map((a, i) => (
                  <Link key={a.id} to={`/article/${a.slug}`} className="flex gap-3 group" data-testid={`sidebar-trending-${a.id}`}>
                    <span className="font-outfit font-black text-2xl text-[#E30613] w-7 shrink-0">{i + 1}</span>
                    <h4 className="font-devanagari text-sm font-bold leading-snug line-clamp-3 group-hover:text-[#E30613]">{a.title}</h4>
                  </Link>
                ))}
              </div>
            </div>

            <div className="rounded-2xl bodh-gradient p-6 text-white">
              <h4 className="font-devanagari font-black text-xl leading-tight">रोज की बोध-डायजेस्ट पाइए</h4>
              <p className="text-white/90 font-devanagari text-sm mt-1.5">हमारा हिंदी न्यूजलेटर सब्सक्राइब करें.</p>
              <Link to="/#newsletter" className="mt-4 inline-flex bg-white text-zinc-900 font-outfit font-bold text-sm px-4 py-2 rounded-full" data-testid="sidebar-newsletter-cta">सब्सक्राइब करें</Link>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
